import { FC, memo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { TruncatingTypography } from '@components';
import { EditOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AccessDetailsItem } from './AppAccessSidepanel/types';

const ITEM_LABEL_MAX_WIDTH = 120;

const AccessDetailsCard: FC<{ item: AccessDetailsItem }> = memo(({ item }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      minWidth: 0,
      maxWidth: ITEM_LABEL_MAX_WIDTH,
    }}
  >
    <TruncatingTypography fontSize={12} fontWeight={500} sx={{ color: '#162936', lineHeight: 1.2 }}>
      {item.value}
    </TruncatingTypography>
    <TruncatingTypography
      fontSize={14}
      fontWeight={600}
      tooltipTitle={item.label}
      sx={{ color: '#162936', lineHeight: 1.2 }}
    >
      {item.label}
    </TruncatingTypography>
  </Box>
));

AccessDetailsCard.displayName = 'AccessDetailsCard';

const DetailsRow: FC<{ item: AccessDetailsItem }> = ({ item }) => (
  <>
    <Box
      sx={{
        width: '4px',
        height: '4px',
        borderRadius: '50%',
        backgroundColor: '#162936',
        justifySelf: 'center',
      }}
    />
    <Typography fontSize={12} fontWeight={500} sx={{ lineHeight: 1.2, whiteSpace: 'nowrap' }}>
      {item.value}
    </Typography>
    <Typography
      fontSize={14}
      fontWeight={500}
      sx={{ color: '#162936', lineHeight: 1.2, minWidth: 0, ml: 1 }}
    >
      {item.label}
    </Typography>
  </>
);

type EntitlementListDialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  items: AccessDetailsItem[];
};

const EntitlementListDialog: FC<EntitlementListDialogProps> = ({ open, onClose, title, items }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          width: isMobile ? '100%' : '560px',
          borderRadius: '16px',
          padding: '24px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          p: 0,
          fontSize: '1.25rem',
          fontWeight: 700,
          color: '#162936',
          fontFamily: 'Manrope, sans-serif',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {title}
          <Typography
            fontSize={14}
            fontWeight={500}
            sx={{ color: '#6C6C6C', lineHeight: '1.25rem' }}
          >
            Showing all
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ mt: '-4px' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, mt: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '8px max-content minmax(0, 1fr)',
            columnGap: '12px',
            rowGap: '16px',
            alignItems: 'center',
            maxHeight: isMobile ? '100%' : '350px',
            overflow: 'auto',
            backgroundColor: '#F0F0F1',
            padding: '12px',
            borderRadius: '8px',
          }}
        >
          {items.map((item) => (
            <DetailsRow key={item.value} item={item} />
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const PREVIEW_COUNT = 4;

interface AccessSectionProps {
  title: string;
  addLabel: string;
  items: AccessDetailsItem[];
  isEditable?: boolean;
  onEdit?: () => void;
}

const AccessSection: FC<AccessSectionProps> = ({
  title,
  addLabel,
  items,
  isEditable = false,
  onEdit,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', py: '1rem' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography fontSize={16} fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
              {title}
            </Typography>
            {isEditable && (
              <Button
                variant="text"
                disableRipple
                sx={{
                  textTransform: 'none',
                  p: 0,
                  marginLeft: items.length > 0 ? 0 : 'auto',
                  color: '#51B8B0',
                  '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' },
                }}
                onClick={onEdit}
              >
                {items.length > 0 ? (
                  <EditOutlined sx={{ fontSize: '1.5rem' }} />
                ) : (
                  <>
                    <AddIcon sx={{ height: '1rem', width: '1rem' }} />
                    <Typography fontSize={14} fontWeight={700} sx={{ color: '#51B8B0' }}>
                      {addLabel}
                    </Typography>
                  </>
                )}
              </Button>
            )}
          </Box>
        </Box>

        {items.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
            {items.slice(0, PREVIEW_COUNT).map((item) => (
              <AccessDetailsCard key={item.value} item={item} />
            ))}
            {items.length > PREVIEW_COUNT && (
              <Button
                variant="text"
                disableRipple
                sx={{
                  textTransform: 'none',
                  p: 0,
                  color: '#51B8B0',
                  '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' },
                }}
                onClick={() => setIsDialogOpen(true)}
              >
                {`Show all (${items.length})`}
              </Button>
            )}
          </Box>
        )}
      </Box>

      <EntitlementListDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title={title}
        items={items}
      />
    </>
  );
};

export default AccessSection;
